import { useState, useEffect, useRef } from "react";

/**
 * Tracks scroll position with minimal re-renders:
 * - `scrolled` only flips when crossing the 80px threshold (≈ 2× per session)
 * - `progress` lives in a ref + DOM CSS variable so consumers can read it via
 *   `var(--scroll-progress)` without forcing React re-renders on every scroll
 */
export default function useScrollProgress() {
  const [scrolled, setScrolled] = useState(false);
  const progressRef = useRef(0);

  useEffect(() => {
    let ticking = false;
    let lastScrolled = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const total = document.body.scrollHeight - window.innerHeight;
        const pct = total > 0 ? (window.scrollY / total) * 100 : 0;
        progressRef.current = pct;
        // Expose progress via CSS variable for consumers that just want
        // a visual indicator (no JS re-render needed).
        document.documentElement.style.setProperty(
          "--scroll-progress",
          `${pct}%`,
        );
        const nextScrolled = window.scrollY > 80;
        if (nextScrolled !== lastScrolled) {
          lastScrolled = nextScrolled;
          setScrolled(nextScrolled);
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { progress: progressRef, scrolled };
}
